<script setup>
import { ref, computed, provide } from 'vue';
import { Virtualizer } from 'virtua/vue';
import { useBreakpoints } from '@vueuse/core';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import ConversationItem from './ConversationItem.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  conversationList: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  showEndOfListMessage: { type: Boolean, default: false },
  label: { type: String, default: '' },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  conversationType: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const emit = defineEmits(['loadMore']);

const conversationListRef = ref(null);
const virtualListRef = ref(null);
const isContextMenuOpen = ref(false);

provide('contextMenuElementTarget', virtualListRef);

const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const showInboxName = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);

const breakpoints = useBreakpoints({
  lg: wootConstants.LARGE_SCREEN_BREAKPOINT,
});
const isLgScreen = breakpoints.greaterOrEqual('lg');
const showExpandedCards = computed(
  () => props.isOnExpandedLayout && isLgScreen.value
);

useChatListKeyboardEvents(conversationListRef);

const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

const onContextMenuToggle = state => {
  isContextMenuOpen.value = state;
};

const loadMoreConversations = () => {
  emit('loadMore');
};

provide('toggleContextMenu', onContextMenuToggle);

defineExpose({ conversationListRef });
</script>

<template>
  <div
    ref="conversationListRef"
    class="flex-1 min-h-0 overflow-y-auto conversations-list"
    :class="{ '!overflow-hidden': isContextMenuOpen }"
  >
    <!-- Table Header for Expanded Layout -->
    <div
      v-if="showExpandedCards"
      class="sticky top-0 z-[1] grid gap-4 items-center px-3 h-8 border-b border-n-slate-3 bg-n-surface-1 text-[11px] font-semibold text-n-slate-10 uppercase tracking-wider grid-cols-[minmax(0,2fr)_max-content]"
    >
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <div class="w-4 flex-shrink-0"></div>
        <div class="w-px h-3 bg-n-slate-6 flex-shrink-0" />
        <div class="w-4 flex-shrink-0 text-center" title="Priority">P</div>
        <div class="w-4 flex-shrink-0 text-center" title="Assignee">A</div>
        <div class="w-4 flex-shrink-0 text-center" title="Status">S</div>
        <div class="w-px h-3 bg-n-slate-6 flex-shrink-0" />
        <div v-if="showInboxName" class="w-20 flex-shrink-0 text-center">Inbox</div>
        <div v-if="showInboxName" class="w-px h-3 bg-n-slate-6 flex-shrink-0" />
        
        <div class="h-6 flex items-center gap-1 max-w-20 w-full min-w-0 flex-shrink-0">
          <div class="size-3.5 flex-shrink-0"></div>
          <span class="truncate text-center w-full">ID</span>
        </div>
        
        <div class="w-[28px] flex-shrink-0"></div>
        <div class="w-32 flex-shrink-0 text-center">Contact</div>
        <div class="flex-1 text-left">Message</div>
      </div>
      
      <div class="flex items-center justify-end gap-1.5 flex-shrink-0">
        <div class="min-w-0 w-24 text-right">Labels</div>
        <div class="w-[5.5rem] text-right pr-4">Time</div>
      </div>
    </div>

    <Virtualizer
      ref="virtualListRef"
      v-slot="{ item }"
      :data="conversationList"
      class="[&>div:has(+_div_.active)>*]:!border-n-surface-1 [&>div:has(+_div_.selected)>*]:!border-n-surface-1"
    >
      <ConversationItem
        :source="item"
        :label="label"
        :team-id="teamId"
        :folders-id="foldersId"
        :conversation-type="conversationType"
        :show-assignee="showAssignee"
        :show-expanded="showExpandedCards"
      />
    </Virtualizer>
    <div v-if="isLoading" class="flex justify-center my-4">
      <Spinner class="text-n-brand" />
    </div>
    <p v-else-if="showEndOfListMessage" class="p-4 text-center text-n-slate-11">
      {{ $t('CHAT_LIST.EOF') }}
    </p>
    <IntersectionObserver
      v-else
      :options="intersectionObserverOptions"
      @observed="loadMoreConversations"
    />
  </div>
</template>
